// Checkout's `<Image>` resolves to an `<img>`, so the cart widget's inline-SVG recolour cannot
// reach it; `/v1/widget/logo.svg` applies the same rule server-side instead.

/** Default CHECKOUT mark; a PNG, so a merchant who picks no colour keeps exactly what they had. */
export const OP_CE_LOGO_PNG = 'https://storage.googleapis.com/cdn.orderprotection.com/Images/OP%20CE%20Logo.png';

/** The same mark as artwork, which is what makes a colour possible. */
export const OP_CE_LOGO_SVG = 'https://storage.googleapis.com/cdn.orderprotection.com/Images/OP-CE-Logo.svg';

/** Size of the mark's canvas, in px, before a merchant resizes it. Shared by both widget types. */
export const DEFAULT_LOGO_SIZE = 50;

/** Ratio the CHECKOUT PNG's disc fills of its canvas; the SVG runs edge to edge without it. */
export const OP_LOGO_MARK_RATIO = 820 / 1200;

/**
 * The box to draw a cart mark in. Checkout draws the PNG, so its disc covers only the ratio of the
 * canvas; the built-in SVG has no such padding and takes the ratio off its box instead, which is
 * what makes one `logoSize` read the same size in both widgets.
 */
export function cartLogoBoxSize(logoSize: number, isBuiltInMark: boolean): number {
  return isBuiltInMark ? Math.round(logoSize * OP_LOGO_MARK_RATIO) : logoSize;
}

/**
 * ViewBox ratio that draws the SVG at the PNG's size, whose disc fills 820/1200 of its canvas.
 *
 * Not 820/1200 itself because the recolour also strokes the disc, making it 73 units wide, not 72.
 */
export const OP_CE_LOGO_SVG_SCALE = 0.674;

/** Hosts the recolour route may fetch artwork from, so `src` cannot be pointed anywhere else. */
export const WIDGET_LOGO_ALLOWED_HOSTS = [
  'storage.googleapis.com',
  'cdn.orderprotection.com',
  'op-media-asset.s3.us-west-1.amazonaws.com',
];

/** A colour is only ever a hex literal, so it cannot inject CSS into the document we serve. */
export const WIDGET_LOGO_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** Sources the route can fetch and recolour; `data:` and raster cannot be, so they keep their own art. */
export function isRecolourableLogo(logoUrl?: string | null): boolean {
  const src = (logoUrl ?? '').trim();

  // Empty means the built-in mark, which we hold as artwork.
  if (!src) return true;

  return /^https:\/\//i.test(src) && /\.svg(?:[?#]|$)/i.test(src);
}

/** The URL to point a CHECKOUT logo at; with no colour this is the artwork itself, never our API. */
export function buildWidgetLogoSource(options: {
  apiBaseUrl: string;
  logoUrl?: string | null;
  logoColor?: string | null;
}): string {
  const custom = (options.logoUrl ?? '').trim();
  const color = (options.logoColor ?? '').trim();

  if (!color || !isRecolourableLogo(custom)) return custom || OP_CE_LOGO_PNG;

  const params = new URLSearchParams({ src: custom || OP_CE_LOGO_SVG, color });

  // Only the built-in mark is corrected; merchant artwork is drawn at whatever size they drew it.
  if (!custom) params.set('scale', String(OP_CE_LOGO_SVG_SCALE));

  return `${options.apiBaseUrl.replace(/\/+$/, '')}/v1/widget/logo.svg?${params.toString()}`;
}
